'use client';
/* eslint-disable @next/next/no-img-element */
/* eslint-disable import/no-extraneous-dependencies */
import { generateStockLinkBySymbol } from '@/utils/links';
import { formatNumberWithUnit } from '@/utils/number';
import { QuotesCodeMapping } from '@/constants/quote/quote';
import { getStockFuture, getStockMarket } from '@/utils/stock';
import { formatTime } from '@/utils/time';
import classnames from 'classnames';
import { CustomEventName, GTMEventKey } from '@/utils/gtm';
import dynamic from 'next/dynamic';
import { GTMButton } from '@/components/atoms/GTMButton';
import { RectangleSkeleton } from '@/components/atoms/Skeleton';
import { getBaseline, getChartUpdateKey, getLastUpdateTimestamp, reverseHistory } from './helper';
import { type History } from '@/types/history';
import Image from 'next/image';
import AnueLogo from '@/public/assets/logo/anue.svg';

const SvgChartV2 = dynamic(() => import('./SvgChart/SvgChartV2'), {
  ssr: false,
  loading: () => <RectangleSkeleton height={64} width='100%' />
});

interface TrendChartProperty {
  data: History;
  showUpdateTime?: boolean;
  timezoneOffsetHour?: number;
  gtmEvent?: {
    [GTMEventKey.EVENT_NAME]: CustomEventName;
    [GTMEventKey.SECTION]: string;
    [GTMEventKey.CLICK_ITEM]: string;
  };
  width?: number;
  height?: number;
}

type Quotes = {
  [QuotesCodeMapping.SYMBOL]: string;
  [QuotesCodeMapping.CHINESE_NAME]: string;
  [QuotesCodeMapping.ENGLISH_NAME]: string;
  [QuotesCodeMapping.CLOSE]: string;
  [QuotesCodeMapping.CHANGE_V2]: string;
  [QuotesCodeMapping.CHANGE_PERCENT_V2]: string;
  [QuotesCodeMapping.LAST_FORMAT]: string;
  [QuotesCodeMapping.NET_CHANGE_FORMAT]: string;
  [QuotesCodeMapping.CHANGE_PERCENT]: number;
  [QuotesCodeMapping.VOLUME]: string;
  isTrading: boolean;
};

export const TrendChart: React.FC<TrendChartProperty> = ({
  data,
  showUpdateTime = true,
  timezoneOffsetHour = 0,
  gtmEvent,
  width = 172,
  height = 64
}) => {
  const { quote = {} as Quotes, session } = data;
  const { c, t } = reverseHistory({
    c: data?.c ?? [],
    t: data?.t ?? []
  });

  const state = {
    windowSize: undefined,
    highlight: true,
    currentValue: undefined
  };

  const { lastUpdateTimestamp, lastPrice } = getLastUpdateTimestamp({ c, t });

  const chartUpdateKey = getChartUpdateKey({
    windowSize: state.windowSize,
    lastUpdateTimestamp,
    lastPrice,
    isTrading: quote?.isTrading
  });

  const base = getBaseline({ quote, c });
  const updateTime =
    (showUpdateTime && lastUpdateTimestamp && lastUpdateTimestamp + timezoneOffsetHour * 60 * 60) || null;

  const renderQuotes = () => {
    // API 欄位: https://cnyesrd.atlassian.net/wiki/spaces/PS/pages/633766431/API
    const {
      [QuotesCodeMapping.SYMBOL]: symbol,
      [QuotesCodeMapping.CHINESE_NAME]: name,
      [QuotesCodeMapping.LAST_FORMAT]: value,
      [QuotesCodeMapping.NET_CHANGE_FORMAT]: netChangeFormat,
      [QuotesCodeMapping.CHANGE_PERCENT]: changePercentage,
      [QuotesCodeMapping.VOLUME]: volume
      // [QUOTES_CODE_MAPPING.MarketStatus]: marketStatus, // 0:收盤, 1:清盤, 2:開盤, 3:盤中暫停交易
    } = quote;

    const change = parseFloat(netChangeFormat);
    const market = getStockMarket(symbol);
    const link = generateStockLinkBySymbol(symbol);
    const fontColor = change > 0 ? 'text-[#e03f19]' : change < 0 ? 'text-[#208306]' : 'text-[#777777]';
    const sign = change > 0 ? '+' : '';
    const fontBackground = change > 0 ? 'bg-[#e03f191a]' : change < 0 ? 'bg-[#2083061a]' : 'bg-[#f2f2f2]';

    const renderNameAndPrice = () => {
      return (
        <div className='flex h-[18px] items-center justify-between hover:text-[#e03f19]'>
          {link ? (
            <GTMButton gtmEvent={gtmEvent}>
              <a className='font-semibold hover:font-semibold' href={link}>
                {name}
              </a>
            </GTMButton>
          ) : (
            <span className='font-semibold'>{name}</span>
          )}
          <span className={classnames('p-[2px]', fontColor, fontBackground)}>
            {sign}
            {change}
          </span>
        </div>
      );
    };

    const renderValueAndPercent = () => {
      return (
        <div className='flex h-[18px] items-center justify-between'>
          <span className={classnames('font-bold', fontColor)}>{value}</span>
          <span className={classnames('p-[2px]', fontColor, fontBackground)}>
            {sign}
            {changePercentage?.toFixed(2)}%
          </span>
        </div>
      );
    };

    const renderVolume = () => {
      let volumeLabel = '成交量';
      let unit = '';

      if (market === 'TWS') {
        unit = '張';
      }

      // 指數類型商品或虛擬貨幣改為"成交額";
      if (getStockFuture(symbol) === 'INDEX' || market === 'CC') {
        volumeLabel = '成交額';
        // 台幣兌換美金
        // 由於新台幣是來自於台北外匯中心的報價，因此揭露的成交額是有特殊意義的。
        // 而過往為每15分鐘揭露，但央行避免炒匯行為，改為上下午盤2筆報價，分別在12:00/16:00兩個時間點做累積報價
        // https://cnyesrd.atlassian.net/browse/ANUE-6129
      } else if (symbol === 'FX:USDTWD:FOREX') {
        if (t.length > 0) {
          const nowTime = t[t.length - 1];
          const today = new Date(nowTime * 1000);
          const day = +today.getDay();
          const hour = +today.getHours();
          const minute = +today.getMinutes();

          // 只需在星期一到星期五的 12 點和 16 點這中間驗證
          // 早上的累積額是在中午 12 點統計出來
          // 全日的累積額是在下午 16 點統計，但考慮後端的線圖分線，提前 5 分鐘做為判斷
          if ([1, 2, 3, 4, 5].includes(day)) {
            const time = hour * 100 + minute;
            const morning = 1200;
            const afternoon = 1555;

            if (time >= morning && time < afternoon) {
              volumeLabel = '上午成交額';
              // 其餘時間就顯示回"全日成交額"
            } else if (time >= afternoon) {
              volumeLabel = '全日成交額';
            }
            // needReduceFontSize = true;
            unit = volume !== '--' ? '美元' : '';
          }
        }
      }

      if (!volume) return <div className='h-[18px] w-full' />;

      return (
        <div className='flex h-[18px] items-center justify-between text-[#777777]'>
          <span>{volumeLabel}</span>
          <span className='bg-[#f2f2f2] p-[2px]'>{`${formatNumberWithUnit(volume)}${unit}`}</span>
        </div>
      );
    };

    return (
      <div className='mb-[6px] flex flex-col gap-1 text-[13px] text-[#383838]'>
        {renderNameAndPrice()}
        {renderValueAndPercent()}
        {renderVolume()}
      </div>
    );
  };

  const renderChart = () => {
    return (
      <SvgChartV2
        updateKey={chartUpdateKey}
        height={height}
        width={width}
        padding={0}
        yGap={0}
        t={t}
        c={c}
        base={base}
        session={session}
        highlightLastPoint={state.highlight}
        tooltipByDate={false}
      />
    );
  };

  const renderDateAndLogo = () => {
    return (
      <div className='mt-1 flex h-[15px] items-center justify-between'>
        <time className='text-xs text-[#9b9b9b]'>{formatTime(updateTime, 'MMDDHHMM', '/')}</time>
        <Image src={AnueLogo} alt='anue logo' style={{ width: '30px', height: 'auto' }} />
      </div>
    );
  };

  return (
    <div className='p-2'>
      {renderQuotes()}
      {renderChart()}
      {renderDateAndLogo()}
    </div>
  );
};

export default TrendChart;
